import { Campaign, useCampaign, useCampaigns } from '@/hooks/useCampaigns';
import { createContext } from '@/utils/context';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

interface CampaignContextType {
  campaigns: Campaign[];
  activeCampaign?: Campaign;
  setActiveCampaign: (campaignId: string | 'all') => void;
  isChangingCampaign: boolean;
  isLoading: boolean;
}

const [useCampaignContext, CampaignContextProvider] =
  createContext<CampaignContextType>('CampaignContext');

export const CampaignProvider = ({ children }: PropsWithChildren) => {
  const {
    data: campaigns,
    isLoading: isLoadingCampaigns,
    isRefetching: isRefetchingCampaigns,
  } = useCampaigns();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isChangingCampaign, setIsChangingCampaign] = useState(false);
  const location = useLocation();

  // Initialize selectedCampaignId from URL or default to 'all'
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | 'all'>(
    searchParams.get('campaign') || 'all',
  );

  // Single effect to manage URL and campaign state synchronization
  useEffect(() => {
    const campaignId = searchParams.get('campaign');
    const shouldBeAll = location.pathname === '/calendar' && !campaignId;

    if (shouldBeAll && selectedCampaignId !== 'all') {
      setIsChangingCampaign(true);
      setSelectedCampaignId('all');
      searchParams.delete('campaign');
      setSearchParams(searchParams);
      setIsChangingCampaign(false);
    } else if (campaignId && campaignId !== selectedCampaignId) {
      setIsChangingCampaign(true);
      setSelectedCampaignId(campaignId);
      setIsChangingCampaign(false);
    }
  }, [searchParams, location.pathname, selectedCampaignId, setSearchParams]);

  const handleSetActiveCampaign = useCallback(
    async (campaignId: string | 'all') => {
      try {
        setIsChangingCampaign(true);
        if (campaignId === 'all') {
          searchParams.delete('campaign');
        } else {
          searchParams.set('campaign', campaignId);
        }
        setSearchParams(searchParams);
      } catch (error) {
        console.error('Failed to update active campaign:', error);
      } finally {
        setIsChangingCampaign(false);
      }
    },
    [searchParams, setSearchParams],
  );

  // Only fetch specific campaign data if a campaign is selected
  const { data: specificCampaign, isLoading: isLoadingSpecificCampaign } =
    useCampaign(selectedCampaignId === 'all' ? '' : selectedCampaignId);

  const activeCampaign = useMemo(() => {
    if (selectedCampaignId === 'all' || !campaigns) return undefined;

    return (
      specificCampaign ||
      campaigns.find((campaign) => campaign.id === selectedCampaignId)
    );
  }, [campaigns, selectedCampaignId, specificCampaign]);

  const value = useMemo(
    () => ({
      campaigns: campaigns ?? [],
      activeCampaign,
      setActiveCampaign: handleSetActiveCampaign,
      isChangingCampaign,
      isLoading:
        isLoadingCampaigns ||
        (selectedCampaignId !== 'all' && isLoadingSpecificCampaign) ||
        isRefetchingCampaigns,
    }),
    [
      campaigns,
      activeCampaign,
      handleSetActiveCampaign,
      isChangingCampaign,
      isLoadingCampaigns,
      selectedCampaignId,
      isLoadingSpecificCampaign,
      isRefetchingCampaigns,
    ],
  );

  return (
    <CampaignContextProvider value={value}>{children}</CampaignContextProvider>
  );
};

export { useCampaignContext };
